import type { CSSProperties } from 'react';
import type { Temper, TextAlign, WritingChunks } from '@/types/frontend-types';

/** How the log block sits in its viewport, and whether its edges soften. */
export enum CbullshitcodingFlow {
  /** The default: the block is centred behind a 20%/80% vertical edge mask. */
  CenteredSoftEdges = 'centered-soft-edges',
  /**
   * A long log reads from the top edge with no mask — with six or more lines
   * the centred mask fades the first and last line and the block can spill.
   */
  TopUnmasked = 'top-unmasked',
}

export enum CbullshitcodingTimingEventType {
  CycleStart = 'cycle-start',
  LineStart = 'line-start',
  LineComplete = 'line-complete',
  CycleComplete = 'cycle-complete',
}

export interface CbullshitcodingTimingEvent {
  type: CbullshitcodingTimingEventType;
  timestampMs: number;
  stringCount: number;
  writingChunks: WritingChunks;
  charTypeSpeedMs: number;
  wordTypeSpeedMs: number;
  pauseBetweenLinesMs: number;
  stringIndex: number | null;
  lineLength: number | null;
  wordCount: number | null;
  lineElapsedMs: number | null;
  cycleElapsedMs: number | null;
}

export interface CbullshitcodingProps {
  /** Path to a text file to load strings from (e.g., '/stalling-code/deploying.txt') */
  codeFile?: string;
  /** Array of strings to display sequentially (used as fallback if codeFile fails) */
  strings?: string[];
  /** Stable reset key for string payloads; prevents equal content from restarting on re-render */
  contentKey?: string;
  /** Visual mood/tone for text color (good=accent, neutral=white, bad=danger) */
  temper?: Temper;
  /** Animation granularity - how text is revealed (letter by letter, word by word, or line by line) */
  writingChunks?: WritingChunks;
  /** Animation speed multiplier (1.0 = default speed, higher = slower, lower = faster) */
  speed?: number;
  /** Text alignment (left or center) */
  align?: TextAlign;
  /** Whether to loop the animation after completing all strings (default: true) */
  loop?: boolean;
  /** Whether to clear the display with empty lines when animation ends (only applies when loop=false, default: false) */
  clearOnEnd?: boolean;
  style?: CSSProperties;
  /** Optional style overrides for fade wrapper */
  fadeWrapperStyle?: CSSProperties;
  /** Text size for log lines */
  textSize?: number;
  /** Callback fired when the current strings array completes a full cycle */
  onCycleComplete?: () => void;
  /** Base typing speed per character before applying speed multiplier */
  charTypeSpeedMs?: number;
  /** Base typing speed per word before applying speed multiplier */
  wordTypeSpeedMs?: number;
  /** Pause between lines */
  pauseBetweenLinesMs?: number;
  /** Fade duration for transitions between string sets */
  fadeDurationMs?: number;
  /** Optional timing callback for runtime instrumentation */
  onTimingEvent?: (event: CbullshitcodingTimingEvent) => void;
  /** Empty terminal lines appended after a loaded file or provided strings finish */
  trailingLineDropCount?: number;
  /** Pause used specifically between appended trailing line drops */
  trailingLineDropPauseMs?: number;
  /** Hard cap for rendered line length; useful for fixed-width terminal art */
  maxDisplayLineLength?: number;
  /** Remove shared left padding from all non-empty lines before rendering */
  trimCommonIndent?: boolean;
  /**
   * Center the whole text block horizontally as a single unit. Lines stay
   * left-aligned within the block so internal geometry is preserved. Use for
   * centered ASCII art / logos rather than per-line `align`, which distorts art.
   */
  centerBlock?: boolean;
  /** Substitute {{KEY}} placeholders before parsing; values may carry colour markup. */
  replacements?: Record<string, string>;
  /** Vertical flow of the log block; defaults to centred with soft edges. */
  flow?: CbullshitcodingFlow;
}
